import {onMounted, Ref, ref, watch} from '@vue/composition-api'
import {debounce} from 'lodash'

import apiOffers from '@/api/offers.api'
import {mapResponseDataToOffers} from '@/mappers/offers'
import {Status} from '@/types/offers'

interface OffsetPagination {
  from: number
  size: number
}

interface Props {
  dealerId: string
  statuses: Ref<Status[]>
  text: Ref<string>
  pagination: Ref<OffsetPagination>
}

export const useExpiringOffersList = <T>(props: Props) => {
  const list = ref<Array<T>>([]) as Ref<Array<T>>
  const total = ref(0)
  const isLoading = ref(false)

  async function fetchExpiringOffers() {
    if (!props.statuses.value.includes(Status.Accepted)) {
      list.value = []
      total.value = 0
      return
    }

    isLoading.value = true

    try {
      const response = await apiOffers.getExpiringOffersByDealerId(props.dealerId, {
        text: cleanSearchInput(props.text.value),
        from: props.pagination.value.from,
        size: props.pagination.value.size,
      })

      const data = mapResponseDataToOffers(response.hits || response.data)
      list.value = data || []
      total.value = response.total?.value ?? data.length
    } catch (e) {
      console.error('Error fetching the expiring offer list', e)
      list.value = []
      total.value = 0
    } finally {
      isLoading.value = false
    }
  }

  const debouncedFetch = debounce(fetchExpiringOffers, 500)

  onMounted(fetchExpiringOffers)

  watch([props.statuses, props.text], debouncedFetch, {deep: true})

  function cleanSearchInput(input?: string) {
    if (!input) return ''
    return input.trim().replace(/^\+/, '')
  }

  return {
    data: list,
    total,
    isLoading,
    refetch: fetchExpiringOffers,
  }
}
